import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { CARD, INPUT, PRIMARY, SECONDARY } from './ui';

interface Props {
  hasDraft: boolean;
  isOnline: boolean;
  isDrafting: boolean;
  onDraft(args: { guidance?: string; redraft: boolean; keepPhotos: boolean }): Promise<boolean>;
}

/** Generate the first draft; once a draft exists, only an explicit Redraft replaces it. */
export function DraftCard({ hasDraft, isOnline, isDrafting, onDraft }: Props) {
  const [guidance, setGuidance] = useState('');
  const [showRedraft, setShowRedraft] = useState(false);
  const [keepPhotos, setKeepPhotos] = useState(true);

  const run = async (redraft: boolean) => {
    const ok = await onDraft({ guidance: guidance.trim() || undefined, redraft, keepPhotos });
    if (ok) { setGuidance(''); setShowRedraft(false); }
  };
  const guidanceBox = (placeholder: string) => (
    <textarea value={guidance} onChange={(e) => setGuidance(e.target.value)} maxLength={1000} rows={2} placeholder={placeholder} className={INPUT} />
  );

  if (!hasDraft) {
    return (
      <div className="space-y-3">
        {guidanceBox("Anything to emphasize? (optional — e.g. 'stress the undersized panel')")}
        <button type="button" onClick={() => void run(false)} disabled={!isOnline || isDrafting} className={`${PRIMARY} min-h-[52px] w-full rounded-2xl`}>
          {isDrafting ? <><Loader2 size={18} className="animate-spin" /> AI is writing your report...</> : <><Sparkles size={18} /> Generate Report with AI</>}
        </button>
      </div>
    );
  }
  return (
    <div className={`${CARD} space-y-3`}>
      {showRedraft ? (
        <>
          {guidanceBox("Direction for the new draft (e.g. 'focus on fuel conversion, shorter')")}
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={keepPhotos} onChange={(e) => setKeepPhotos(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
            Keep my photo selections
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => void run(true)} disabled={!isOnline || isDrafting} className={`${PRIMARY} flex-1`}>
              {isDrafting ? <><Loader2 size={16} className="animate-spin" /> Redrafting...</> : <><Sparkles size={16} /> Redraft</>}
            </button>
            <button type="button" onClick={() => setShowRedraft(false)} className={SECONDARY}>Cancel</button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => setShowRedraft(true)} disabled={!isOnline} className={`${SECONDARY} w-full`}>
          <Sparkles size={16} /> Redraft with AI (give direction)
        </button>
      )}
    </div>
  );
}
